import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, PlugZap } from "lucide-react";
import { MyDataStatusBanner } from "@/components/MyDataStatusBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mydataApi, mydataError } from "@/api";
import { useLang } from "@/i18n";

export const MyDataSettings = () => {
  const { t } = useLang();
  const [s, setS] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [auth, setAuth] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    mydataApi.settings().then((r) => { setS(r); setSaved(r); });
    mydataApi.overview().then((r) => setAuth(r.auth)).catch(() => {});
  }, []);

  const dirty = s && saved && (s.mode !== saved.mode || s.user_id !== saved.user_id || String(s.branch) !== String(saved.branch) || key.trim());

  const testConn = async () => {
    setTesting(true);
    try {
      const r = await mydataApi.testConnection();
      setAuth(r.mode === "mock" ? null : r);
      if (r.ok) toast.success(r.mode === "mock" ? t("conn_mock") : t("conn_ok"));
      else toast.error(t("auth_fail_banner"));
    } catch (e) { toast.error(mydataError(e, t)); } finally { setTesting(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await mydataApi.saveSettings({ mode: s.mode, user_id: s.user_id, branch: Number(s.branch) || 0, subscription_key: key.trim() ? key.trim() : null });
      setS(saved); setSaved(saved); setKey(""); setAuth(null);
      toast.success(t("settings_saved"));
    } catch { toast.error(t("error")); } finally { setSaving(false); }
  };

  if (!s) return null;
  return (
    <form onSubmit={submit} className="erp-card p-6 max-w-3xl space-y-5 page-enter" data-testid="mydata-settings-card">
      <div className="flex items-center gap-2">
        <Landmark size={18} className="text-blue-700" />
        <h2 className="text-xl font-heading font-bold tracking-tight">{t("mydata_title")}</h2>
      </div>
      <p className="text-sm text-slate-500">{t("mydata_settings_hint")}</p>
      {saved && <MyDataStatusBanner mode={saved.mode} auth={auth} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-500">{t("mydata_mode")}</Label>
          <Select value={s.mode} onValueChange={(v) => setS({ ...s, mode: v })}>
            <SelectTrigger data-testid="mydata-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mock">{t("mode_mock")}</SelectItem>
              <SelectItem value="dev">{t("mode_dev")}</SelectItem>
              <SelectItem value="production">{t("mode_production")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-500">{t("mydata_branch")}</Label>
          <Input type="number" min="0" value={s.branch} onChange={(e) => setS({ ...s, branch: e.target.value })} className="font-mono" data-testid="mydata-branch-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-500">{t("mydata_user_id")}</Label>
          <Input value={s.user_id} onChange={(e) => setS({ ...s, user_id: e.target.value })} className="font-mono" data-testid="mydata-user-id-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate-500">{t("mydata_key")} {s.has_key && <span className="text-emerald-600 normal-case">({s.key_hint})</span>}</Label>
          <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={s.has_key ? t("mydata_key_hint") : ""} className="font-mono" data-testid="mydata-key-input" autoComplete="off" />
        </div>
      </div>
      <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-end gap-2">
        {dirty && <span className="text-xs text-amber-700 sm:mr-auto" data-testid="mydata-save-before-test-hint">{t("save_before_test")}</span>}
        <Button type="button" variant="outline" onClick={testConn} disabled={testing || saving || !!dirty} data-testid="mydata-test-connection-button">
          <PlugZap size={15} className={`mr-1 ${testing ? "animate-pulse" : ""}`} />{testing ? t("testing_conn") : t("test_connection")}
        </Button>
        <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="mydata-settings-submit-button">{t("save")}</Button>
      </div>
    </form>
  );
};
